import React, { useState, useEffect, useRef } from 'react';
import { MoreIcon } from '../icons';

export interface DropdownAction {
  label: string;
  icon?: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

interface ActionDropdownProps {
  actions: DropdownAction[];
  id: string; // Unique identifier for the dropdown
  className?: string;
}

export function ActionDropdown({ actions,  className = '' }: ActionDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleActionClick = (action: DropdownAction, e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    action.onClick(e);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        className="text-gray-500 hover:text-gray-800 p-2"
        onClick={handleToggle}
        title="Actions"
      >
        <MoreIcon className="w-5 h-5" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
          <p className='px-4 text-left text-sm font-bold items-center border-b py-2'>Menu</p>
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={(e) => handleActionClick(action, e)}
              disabled={action.disabled}
              className={`
                w-full text-left px-4 py-2 text-sm flex items-center gap-2
                disabled:opacity-50 disabled:cursor-not-allowed
                ${action.variant === 'danger'
                  ? 'text-red-600 hover:bg-red-50'
                  : 'text-gray-700 hover:bg-gray-50'
                }
              `}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ActionDropdown;
